import { z } from "zod";

/**
 * Contact form validation schema with security-focused constraints
 */
export const contactFormSchema = z.object({
  firstname: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, hyphens, and apostrophes"),
  
  lastname: z
    .string()
    .trim()
    .min(1, "Last name is required")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes"),
  
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters")
    .toLowerCase()
    .refine(
      (email) => {
        const personalEmailDomains = [
          'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
          'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
          'yandex.com', 'zoho.com', 'gmx.com', 'live.com', 'msn.com'
        ];
        const domain = email.split('@')[1];
        return !personalEmailDomains.includes(domain);
      },
      { message: "Please use a work email address" }
    ),
  
  company: z
    .string()
    .trim()
    .max(100, "Company name must be less than 100 characters")
    .optional()
    .or(z.literal("")),
  
  phone: z
    .string()
    .trim()
    .regex(
      /^[\d\s\-\+\(\)]*$/,
      "Phone number can only contain numbers, spaces, and basic formatting characters"
    )
    .max(20, "Phone number must be less than 20 characters")
    .optional()
    .or(z.literal("")),
  
  message: z
    .string()
    .trim()
    .min(10, "Message must be at least 10 characters")
    .max(2000, "Message must be less than 2000 characters"),
  
  // Honeypot field - should remain empty for legitimate users
  website: z
    .string()
    .max(0, "Spam detected")
    .optional()
    .or(z.literal("")),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;
